import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Quote } from "lucide-react";
import { IMAGES, STATS, TESTIMONIALS } from "@/data/content";
import { Reveal, SectionLabel } from "./Reveal";

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-36 bg-[#050A14]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid md:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Parallax image */}
          <div className="md:col-span-6" ref={ref}>
            <Reveal>
              <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5]">
                <motion.img
                  style={{ y: imgY }}
                  src={IMAGES.office}
                  alt="Titan Web Solutions team"
                  className="absolute inset-0 w-full h-[124%] object-cover"
                />
                <div className="absolute inset-0 bg-blue-950/30" />
                <div className="absolute bottom-6 left-6 right-6 rounded-xl backdrop-blur-xl bg-[#0A1428]/70 border border-white/10 p-6">
                  <p className="font-display font-semibold text-white text-lg">
                    A senior team obsessed with your bottom line.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Copy + stats */}
          <div className="md:col-span-6">
            <SectionLabel testid="about-label">Who we are</SectionLabel>
            <Reveal>
              <h2 className="font-display font-bold tracking-tight text-white text-4xl md:text-5xl leading-[1.05]">
                We build for the businesses that <span className="text-[#1E6BFF]">everyone else overlooks.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-gray-400 text-lg leading-relaxed">
                Titan Web Solutions exists for the local shop, the family practice, the
                two-person studio with big ambitions. We give small businesses the kind of
                website usually reserved for companies ten times their size — and we make it
                pay for itself.
              </p>
            </Reveal>

            <div className="mt-10 grid grid-cols-2 gap-6">
              {STATS.map((s, i) => (
                <Reveal key={s.label} delay={0.1 + i * 0.05}>
                  <div
                    data-testid={`about-stat-${s.label}`}
                    className="rounded-xl border border-white/10 bg-white/[0.03] p-6"
                  >
                    <div className="font-display font-bold text-3xl md:text-4xl text-[#1E6BFF]">
                      {s.value}
                    </div>
                    <div className="text-sm text-gray-400 mt-1">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mt-20 md:mt-28 grid md:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div
                data-testid={`testimonial-${i}`}
                className="h-full rounded-2xl border border-white/10 bg-white/[0.03] p-8 md:p-10"
              >
                <Quote size={32} className="text-[#1E6BFF] mb-5" />
                <p className="text-white text-lg md:text-xl leading-relaxed font-display">
                  "{t.quote}"
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="font-semibold text-white">{t.name}</div>
                  <div className="text-sm text-gray-400">{t.role}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
